<!-- NOTICE: KAPTION-INTEGRATION -->
<!-- Modal for viewing and managing scheduled messages in a conversation -->
<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, parseISO } from 'date-fns';
import {
  es as esLocale,
  pt as ptLocale,
  fr as frLocale,
  de as deLocale,
  it as itLocale,
  tr as trLocale,
  enUS as enLocale,
} from 'date-fns/locale';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import KaptionScheduledTemplatePreview from './KaptionScheduledTemplatePreview.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  scheduledMessages: {
    type: Array,
    default: () => [],
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
  isCancelling: {
    type: Boolean,
    default: false,
  },
  accountLevel: {
    type: Boolean,
    default: false,
  },
  searchQuery: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'cancel', 'update:show', 'search']);

const { locale, t } = useI18n();

const confirmDialog = ref(null);

const localSearchQuery = ref(props.searchQuery);

// Template rows arrive with their rendered `template_preview` (see
// KaptionScheduledTemplatePreview), so opening the modal fetches nothing.
watch(
  () => props.show,
  newVal => {
    if (newVal && props.accountLevel) {
      localSearchQuery.value = '';
    }
  }
);

const onSearchInput = event => {
  localSearchQuery.value = event.target.value;
  emit('search', localSearchQuery.value);
};

const localShow = computed({
  get() {
    return props.show;
  },
  set(value) {
    emit('update:show', value);
  },
});

// Get date-fns locale based on current i18n locale
const getDateFnsLocale = () => {
  const lang = locale.value.split('-')[0];
  const locales = {
    en: enLocale,
    es: esLocale,
    pt: ptLocale,
    fr: frLocale,
    de: deLocale,
    it: itLocale,
    tr: trLocale,
  };
  return locales[lang] || enLocale;
};

const formatScheduledTime = isoString => {
  if (!isoString) return '';
  try {
    const date = parseISO(isoString);
    const dateLocale = getDateFnsLocale();
    return format(date, 'PPPP p', { locale: dateLocale });
  } catch (error) {
    return isoString;
  }
};

const onClose = () => {
  emit('close');
};

const onCancelMessage = async message => {
  const ok = await confirmDialog.value.showConfirmation();
  if (ok) {
    if (props.accountLevel) {
      emit('cancel', {
        messageId: message.id,
        conversationId: message.conversation_display_id,
      });
    } else {
      emit('cancel', message.id);
    }
  }
};

const isTemplateMessage = message => message.message_type === 'template';

const getMessageIcon = message => {
  return isTemplateMessage(message)
    ? 'i-lucide-file-text'
    : 'i-lucide-message-square';
};

const getMessageTypeLabel = message => {
  return isTemplateMessage(message)
    ? t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.TEMPLATE_MESSAGE')
    : t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.REGULAR_MESSAGE');
};

const getChannelIcon = channelType => {
  const icons = {
    'Channel::Whatsapp': 'i-lucide-message-circle',
    'Channel::FacebookPage': 'i-lucide-facebook',
    'Channel::TwitterProfile': 'i-lucide-twitter',
    'Channel::Email': 'i-lucide-mail',
    'Channel::WebWidget': 'i-lucide-globe',
    'Channel::Api': 'i-lucide-webhook',
    'Channel::Sms': 'i-lucide-smartphone',
    'Channel::Telegram': 'i-lucide-send',
    'Channel::Line': 'i-lucide-message-square',
  };
  return icons[channelType] || 'i-lucide-inbox';
};
</script>

<template>
  <!-- Account-level: wider, cleaner list layout -->
  <woot-modal
    v-if="accountLevel"
    v-model:show="localShow"
    :on-close="onClose"
    size="account-scheduled-modal"
  >
    <!-- Header with integrated search -->
    <div class="px-10 pt-8 pb-0">
      <h2 class="text-lg font-semibold text-n-slate-12 mb-5">
        {{ $t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.MODAL_TITLE') }}
      </h2>
      <input
        :value="localSearchQuery"
        type="text"
        class="w-full px-4 py-2.5 text-sm rounded-lg border border-n-weak bg-n-alpha-black1 dark:bg-n-alpha-white1 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-blue-7 focus:border-transparent"
        :placeholder="
          $t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.SEARCH_PLACEHOLDER')
        "
        @input="onSearchInput"
      />
    </div>

    <div class="account-modal-content px-10 py-6">
      <!-- Loading state -->
      <div
        v-if="isFetching"
        class="flex flex-col items-center justify-center py-16 gap-4"
      >
        <div
          class="h-8 w-8 border-2 border-n-blue-9 border-t-transparent rounded-full animate-spin"
        />
        <span class="text-sm text-n-slate-11">
          {{ $t('CONVERSATION.LOADING_CONVERSATIONS') }}
        </span>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="scheduledMessages.length === 0"
        class="flex flex-col items-center justify-center py-16 gap-3"
      >
        <span class="i-lucide-calendar-clock size-10 text-n-slate-9" />
        <span class="text-sm text-n-slate-11">
          {{
            localSearchQuery
              ? $t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.EMPTY_SEARCH_STATE')
              : $t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.EMPTY_STATE')
          }}
        </span>
      </div>

      <!-- Messages list -->
      <div v-else class="divide-y divide-n-weak">
        <div
          v-for="message in scheduledMessages"
          :key="message.id"
          class="account-message-row py-5 first:pt-0 last:pb-0"
        >
          <!-- Row top: contact + inbox + time + cancel -->
          <div class="flex items-center justify-between gap-4 mb-2">
            <div class="flex items-center gap-2 min-w-0 flex-1">
              <!-- Contact initial circle -->
              <span
                class="flex-shrink-0 size-7 rounded-full bg-n-alpha-black3 dark:bg-n-alpha-white2 flex items-center justify-center text-xs font-semibold text-n-slate-11"
              >
                {{
                  (
                    message.contact?.name ||
                    message.contact?.phone_number ||
                    '?'
                  )
                    .charAt(0)
                    .toUpperCase()
                }}
              </span>
              <!-- Contact name -->
              <span class="text-sm font-medium text-n-slate-12 truncate">
                {{ message.contact?.name || message.contact?.phone_number }}
              </span>
              <!-- Phone (when name exists) -->
              <span
                v-if="message.contact?.name && message.contact?.phone_number"
                class="text-xs text-n-slate-10 truncate hidden sm:inline"
              >
                {{ message.contact.phone_number }}
              </span>
              <!-- Dot separator -->
              <span class="text-n-slate-8 text-xs">&middot;</span>
              <!-- Inbox -->
              <span
                v-if="message.inbox"
                class="inline-flex items-center gap-1 text-xs text-n-slate-11 truncate"
              >
                <span
                  :class="getChannelIcon(message.inbox.channel_type)"
                  class="size-3.5 flex-shrink-0"
                />
                <span class="truncate">{{ message.inbox.name }}</span>
              </span>
              <!-- Conversation ID -->
              <span
                v-if="message.conversation_display_id"
                class="text-xs text-n-slate-10 flex-shrink-0"
              >
                #{{ message.conversation_display_id }}
              </span>
            </div>

            <!-- Cancel button -->
            <button
              class="flex-shrink-0 p-1.5 rounded-lg text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-ruby-3 transition-colors"
              :disabled="isCancelling"
              @click="onCancelMessage(message)"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>

          <!-- Row bottom: time + message preview -->
          <div class="pl-9">
            <span
              class="flex items-center gap-1.5 text-xs text-n-slate-10 mb-1.5"
            >
              <span class="i-lucide-clock size-3" />
              {{ formatScheduledTime(message.scheduled_at) }}
            </span>
            <KaptionScheduledTemplatePreview
              v-if="isTemplateMessage(message)"
              :message="message"
            />
            <p
              v-else
              class="text-sm text-n-slate-12 leading-relaxed whitespace-pre-wrap break-words mb-0 line-clamp-2"
            >
              {{ message.message_content }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="px-10 py-5 border-t border-n-weak flex justify-end">
      <Button variant="faded" color="slate" @click="onClose">
        {{ $t('CONVERSATION.HEADER.CLOSE') }}
      </Button>
    </div>

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.MODAL_TITLE')"
      :description="
        $t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.CANCEL_CONFIRMATION')
      "
    />
  </woot-modal>

  <!-- Per-conversation: original card-based layout -->
  <woot-modal
    v-else
    v-model:show="localShow"
    :on-close="onClose"
    size="modal-big"
  >
    <woot-modal-header
      :header-title="$t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.MODAL_TITLE')"
    />

    <div class="modal-content-container p-8">
      <!-- Loading state -->
      <div
        v-if="isFetching"
        class="flex flex-col items-center justify-center py-12 gap-4"
      >
        <div
          class="h-8 w-8 border-2 border-n-blue-9 border-t-transparent rounded-full animate-spin"
        />
        <span class="text-sm text-n-slate-11">
          {{ $t('CONVERSATION.LOADING_CONVERSATIONS') }}
        </span>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="scheduledMessages.length === 0"
        class="flex flex-col items-center justify-center py-12 gap-4"
      >
        <Icon icon="i-lucide-calendar-clock" class="size-12 text-n-slate-9" />
        <span class="text-sm text-n-slate-11">
          {{ $t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.EMPTY_STATE') }}
        </span>
      </div>

      <!-- Messages List -->
      <div v-else class="space-y-4">
        <div
          v-for="message in scheduledMessages"
          :key="message.id"
          class="message-item bg-n-alpha-black2 dark:bg-n-alpha-white1 border border-n-weak rounded-xl p-5 flex items-start gap-5"
        >
          <div
            class="message-icon-container bg-n-alpha-black3 dark:bg-n-alpha-white2 p-3 rounded-xl flex-shrink-0"
          >
            <Icon
              :icon="getMessageIcon(message)"
              class="size-6 text-n-blue-11"
            />
          </div>

          <div class="message-details flex-1 min-w-0">
            <div class="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
              <span
                class="text-[10px] font-bold text-n-blue-11 uppercase tracking-widest px-2 py-0.5 bg-n-blue-3 dark:bg-n-blue-11/20 rounded-md"
              >
                {{ getMessageTypeLabel(message) }}
              </span>
              <span class="text-xs text-n-slate-11 font-medium">
                {{
                  $t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.SCHEDULED_FOR', {
                    time: formatScheduledTime(message.scheduled_at),
                  })
                }}
              </span>
            </div>

            <div
              class="bg-n-alpha-black1 dark:bg-n-alpha-white1 rounded-lg p-3 border border-n-weak/50"
            >
              <KaptionScheduledTemplatePreview
                v-if="isTemplateMessage(message)"
                :message="message"
              />
              <p
                v-else
                class="text-sm text-n-slate-12 leading-relaxed whitespace-pre-wrap break-words mb-0"
              >
                {{ message.message_content }}
              </p>
            </div>
          </div>

          <div class="message-actions flex-shrink-0 ml-2">
            <Button
              variant="faded"
              color="ruby"
              size="sm"
              icon="i-lucide-trash-2"
              class="!bg-n-ruby-3 hover:!bg-n-ruby-4 !text-n-ruby-11 border-n-ruby-6"
              :is-loading="isCancelling"
              @click="onCancelMessage(message)"
            >
              {{ $t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.CANCEL') }}
            </Button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-footer px-8 py-6 border-t border-n-weak flex justify-end">
      <Button variant="faded" color="slate" @click="onClose">
        {{ $t('CONVERSATION.HEADER.CLOSE') }}
      </Button>
    </div>

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.MODAL_TITLE')"
      :description="
        $t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.CANCEL_CONFIRMATION')
      "
    />
  </woot-modal>
</template>

<style scoped lang="scss">
/* Per-conversation modal styles */
.modal-content-container {
  max-height: 60vh;
  overflow-y: auto;
}

.message-item {
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    @apply shadow-sm border-n-slate-6;
  }
}

/* Account-level modal styles */
.account-modal-content {
  max-height: 65vh;
  overflow-y: auto;
}

.account-message-row {
  transition: background-color 0.15s ease;
}
</style>

<style lang="scss">
/* Account-level modal: wider than default */
.account-scheduled-modal {
  width: 56rem;
  max-width: 90%;
}
</style>
